import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Globe, Share2, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { fetchPadron, normalizar } from '../lib/padron';
import { mockData } from '../data/mock';
import { PAGES } from '../seo';

const MAILTO = `mailto:${mockData.contact.email}?subject=${encodeURIComponent('Consulta sobre socio UIQ')}&body=${encodeURIComponent(
  'Hola, me contacto desde el padrón de socios de la UIQ. Me interesa contactar a:\n\nNombre de la empresa: \n\nMensaje: '
)}`;

const sinProtocolo = (u) => u.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');

const SocioCard = ({ s }) => {
  const [abierto, setAbierto] = useState(false);
  const lugar = [s.direccion, s.localidad].filter(Boolean).join(' · ');
  const largo = s.actividad.length > 170;

  return (
    <article className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-7 flex flex-col gap-3 transition-all duration-200 hover:border-blue-300 hover:shadow-lg">
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-snug break-words">{s.empresa}</h2>
        {s.rubro && <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-blue-600">{s.rubro}</p>}
      </div>

      {(lugar || s.web || s.redes.length > 0) && (
        <div className="flex flex-col gap-1.5 text-sm sm:text-[15px] text-gray-600">
          {lugar && (
            <div className="flex gap-2.5 items-start">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <span>{lugar}</span>
            </div>
          )}
          {s.web && (
            <div className="flex gap-2.5 items-start">
              <Globe className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <a href={s.web} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline break-all">
                {sinProtocolo(s.web)}
              </a>
            </div>
          )}
          {s.redes.length > 0 && (
            <div className="flex gap-2.5 items-start">
              <Share2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <span>
                {s.redes.map((r, i) => (
                  <React.Fragment key={r.url}>
                    {i > 0 && ' · '}
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 hover:underline">
                      {r.label}
                    </a>
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}
        </div>
      )}

      {s.actividad && (
        <div className="border-t border-gray-100 pt-3 text-sm sm:text-[15px] text-gray-700 leading-relaxed">
          <p className={abierto ? 'whitespace-pre-line' : 'line-clamp-3'}>{s.actividad}</p>
          {largo && (
            <button
              type="button"
              onClick={() => setAbierto(!abierto)}
              className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
            >
              {abierto ? 'Ver menos' : 'Ver más'}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export const Socios = ({ initialSocios = null }) => {
  const [socios, setSocios] = useState(initialSocios);
  const [error, setError] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [rubro, setRubro] = useState('');

  const cargar = () => {
    setError(false);
    setSocios(null);
    fetchPadron().then(setSocios).catch(() => setError(true));
  };

  useEffect(() => {
    document.title = PAGES.socios.title;
    window.scrollTo(0, 0);
    cargar();
    return () => { document.title = PAGES.home.title; };
  }, []);

  const rubros = useMemo(() => {
    if (!socios) return [];
    const cuenta = {};
    socios.forEach((s) => { if (s.rubro) cuenta[s.rubro] = (cuenta[s.rubro] || 0) + 1; });
    return Object.keys(cuenta)
      .sort((a, b) => a.localeCompare(b, 'es'))
      .map((r) => ({ r, n: cuenta[r] }));
  }, [socios]);

  const filtrados = useMemo(() => {
    if (!socios) return [];
    const q = normalizar(busqueda);
    return socios.filter((s) => (!q || s.busqueda.includes(q)) && (!rubro || s.rubro === rubro));
  }, [socios, busqueda, rubro]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main id="contenido-principal" tabIndex={-1} className="pt-20 lg:pt-24 focus:outline-none">
        {/* Encabezado de la página */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Volver al inicio
            </Link>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">Padrón de socios</h1>
            <p className="mt-2 text-base sm:text-lg text-gray-600">Unión Industrial Quilmes — directorio institucional de empresas socias</p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 pb-28">
          {/* Buscador y filtro */}
          <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row gap-3 shadow-sm">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="search"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar empresa, actividad o localidad"
                aria-label="Buscar"
                autoComplete="off"
                className="w-full h-12 pl-11 pr-4 text-base rounded-xl border-2 border-gray-200 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-100 transition"
              />
            </div>
            <select
              value={rubro}
              onChange={(e) => setRubro(e.target.value)}
              aria-label="Filtrar por rubro"
              className="h-12 px-4 text-base rounded-xl border-2 border-gray-200 bg-white focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-100 transition sm:w-72 cursor-pointer"
            >
              <option value="">Todos los rubros</option>
              {rubros.map(({ r, n }) => (
                <option key={r} value={r}>{`${r} (${n})`}</option>
              ))}
            </select>
          </div>

          {/* Estado */}
          {error && (
            <div className="mt-10 text-center bg-white border border-gray-200 rounded-2xl p-10">
              <p className="text-gray-700">No pudimos cargar el padrón en este momento.</p>
              <button onClick={cargar} className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <RefreshCw className="w-4 h-4" /> Reintentar
              </button>
            </div>
          )}

          {!error && !socios && (
            <div className="mt-6 grid md:grid-cols-2 gap-5 sm:gap-6" aria-busy="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-2xl p-7 animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-1/4 mt-3" />
                  <div className="h-3 bg-gray-100 rounded w-full mt-6" />
                  <div className="h-3 bg-gray-100 rounded w-5/6 mt-2" />
                </div>
              ))}
            </div>
          )}

          {socios && (
            <>
              <p className="mt-6 mb-5 text-[15px] text-gray-600" data-testid="padron-count">
                {filtrados.length === socios.length ? (
                  <><b className="text-gray-900">{socios.length}</b> empresas socias</>
                ) : (
                  <><b className="text-gray-900">{filtrados.length}</b> de {socios.length} empresas</>
                )}
              </p>
              {filtrados.length ? (
                <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
                  {filtrados.map((s) => <SocioCard key={s.empresa} s={s} />)}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-16">No se encontraron empresas con ese criterio.</p>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />

      {/* Botón flotante de consulta */}
      <a
        href={MAILTO}
        className="fixed bottom-5 right-4 sm:bottom-7 sm:right-7 z-40 flex items-center justify-center gap-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full shadow-xl shadow-blue-600/30 transition-all hover:-translate-y-0.5 w-14 h-14 sm:w-auto sm:h-auto sm:px-6 sm:py-3.5"
        aria-label="Consultar por una empresa"
      >
        <Mail className="w-5 h-5" />
        <span className="hidden sm:inline">Consultar por una empresa</span>
      </a>
    </div>
  );
};
